import { RuntimeConfigSchema, type RuntimeConfig } from '@ai-secure-twin/shared';

/**
 * Runtime configuration, fetched once at boot from `/config.json` (§6.3).
 *
 * It is deliberately not a `VITE_*` build-time variable: those are baked into the bundle, so
 * the same artefact could not be promoted from dev to prod. `VITE_CONFIG_URL` only says *where*
 * to look.
 */

let current: RuntimeConfig | null = null;

export class ConfigError extends Error {}

/** Where the config lives. Overridable so a developer can point at a deployed environment. */
export function configUrl(): string {
  return import.meta.env.VITE_CONFIG_URL ?? '/config.json';
}

/**
 * Loads and validates the config. Throws rather than falling back to defaults: a console
 * pointed at the wrong user pool or the wrong API is worse than one that refuses to start.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const url = configUrl();

  let response: Response;
  try {
    response = await fetch(url, { cache: 'no-store' });
  } catch (cause) {
    throw new ConfigError(`Could not reach ${url}`, { cause });
  }

  if (!response.ok) {
    throw new ConfigError(`${url} returned HTTP ${response.status}`);
  }

  const parsed = RuntimeConfigSchema.safeParse(await response.json().catch(() => null));
  if (!parsed.success) {
    const fields = parsed.error.issues.map((issue) => issue.path.join('.')).join(', ');
    throw new ConfigError(`${url} is not valid runtime config (${fields})`);
  }

  current = parsed.data;
  return parsed.data;
}

/** The loaded config. Throws if called before `loadRuntimeConfig`, which only happens by mistake. */
export function getRuntimeConfig(): RuntimeConfig {
  if (!current) throw new ConfigError('Runtime config has not been loaded yet');
  return current;
}

/** Test seam. Not used by application code. */
export function setRuntimeConfigForTests(config: RuntimeConfig | null): void {
  current = config;
}
